/**
 * WooCommerce Store Order API response
 * @see {@link https://developer.woocommerce.com/docs/apis/store-api/resources-endpoints/order/#get-order}
 */

import type { PaymentMethodId, ShippingMethodId } from "@/constants/enums";
import type { CartItem } from "@/types/cart";
import type { BillingAddress, ShippingAddress } from "@/types/common";

export enum OrderStatus {
  Draft = "checkout-draft",
  OnHold = "on-hold",
  Pending = "pending",
  Processing = "processing",
  Preparing = "preparing",
  Shipped = "shipped",
  Completed = "completed",
  Refunded = "refunded",
  Failed = "failed",
  Canceled = "canceled",
}

export type OrderDetails = OrderResult & OrderResultAdditionalInfo;

export interface OrderResult {
  id: number;
  status: OrderStatus;
  items: CartItem[];
  coupons: any[];
  fees: any[];
  totals: OrderResultTotalPrices;
  billing_address: BillingAddress;
  shipping_address: ShippingAddress;
  needs_payment: boolean;
  needs_shipping: boolean;
  payment_requirements: string[];
  errors: any[];
  refunds?: OrderRefundsFields[];
}

export interface OrderResultTotalPrices {
  subtotal: string;
  total_discount: string;
  total_shipping: string;
  total_fees: string;
  total_tax: string;
  total_refund: string;
  total_price: string;
  total_items: string;
  total_items_tax: string;
  total_fees_tax: string;
  total_discount_tax: string;
  total_shipping_tax: string;
  tax_lines: any[];
  currency_code: string;
  currency_symbol: string;
  currency_minor_unit: number;
  currency_decimal_separator: string;
  currency_thousand_separator: string;
  currency_prefix: string;
  currency_suffix: string;
}

// 自訂 Order API Response
export interface OrderResultAdditionalInfo {
  number: number;
  date_created: string;
  customer_id: number;
  customer_note: string;
  payment_method: PaymentMethod;
  transaction_id: string;
  date_paid: string;
  date_preparing: string | null;
  date_completed: string | null;
  refunds: any[];
  shipping_methods: ShippingMethod[];
}

interface PaymentMethod {
  method_id: PaymentMethodId;
  method_title: string;
  bank_detail?: BankDetail;
}

interface BankDetail {
  bank_name: string;
  account_number: string;
  account_name: string;
}

interface ShippingMethod {
  method_id: ShippingMethodId;
  method_title: string;
  cvs_details?: {
    cvs_store_id: string;
    cvs_store_name: string;
    cvs_store_address: string;
    cvs_receiver_name?: string;
    cvs_receiver_phone: string;
  };
}

interface OrderRefundsFields {
  id: number;
  reason: string;
  total: string;
}
